import { Injectable } from '@angular/core';
import { 
  DatePeriod} from '@shared/definitions/financial/time-period.model';
import { 
  TimePeriod, 
  DEFAULT_PERIOD_FORMATTERS 
} from '@shared/definitions/financial/time-period.model';
import { 
  CashflowEntry, 
  CashflowMap 
} from '@shared/definitions/financial/cashflow.model';

/**
 * Service responsible for handling time periods in financial calculations
 * Provides utilities for converting between different time periods, generating date periods,
 * and handling period-based calculations
 */
@Injectable({
  providedIn: 'root'
})
export class TimePeriodService {
  
  /**
   * Generate a series of date periods covering a range
   * @param startDate Start of the range
   * @param endDate End of the range
   * @param periodType Type of periods to generate
   * @returns Array of DatePeriod objects covering the range
   */
  generatePeriods(startDate: Date, endDate: Date, periodType: TimePeriod): DatePeriod[] {
    const periods: DatePeriod[] = [];
    const formatter = DEFAULT_PERIOD_FORMATTERS[periodType];
    
    let currentDate = new Date(startDate);
    
    // Ensure dates are at the start of their respective periods
    currentDate = this.normalizeDateToStartOfPeriod(currentDate, periodType);
    const normalizedEndDate = this.normalizeDateToEndOfPeriod(
      new Date(endDate), 
      periodType
    );
    
    while (currentDate <= normalizedEndDate) {
      const periodEndDate = this.calculatePeriodEndDate(currentDate, periodType);
      
      // Create the period
      const period: DatePeriod = {
        startDate: new Date(currentDate),
        endDate: new Date(periodEndDate),
        type: periodType
      };
      
      // Add formatted label
      period.label = formatter.formatPeriodLabel(period);
      
      periods.push(period);
      
      // Move to the next period
      currentDate = this.addToPeriod(new Date(periodEndDate), periodType, 1);
    }
    
    return periods;
  }
  
  /**
   * Convert cashflow entries from one time period to another
   * @param entries Cashflow entries to convert
   * @param targetPeriod The target time period
   * @returns Converted cashflow entries
   */
  convertCashflowPeriod(entries: CashflowEntry[], targetPeriod: TimePeriod): CashflowEntry[] {
    if (!entries || entries.length === 0) {
      return [];
    }
    
    // If entries are already in the target period, just return them
    if (entries.length > 0 && entries[0].period.type === targetPeriod) {
      return entries;
    }
    
    // Get start and end dates covering all entries
    const startDate = entries.reduce(
      (earliest, entry) => entry.period.startDate < earliest ? entry.period.startDate : earliest,
      entries[0].period.startDate
    );
    
    const endDate = entries.reduce(
      (latest, entry) => entry.period.endDate > latest ? entry.period.endDate : latest,
      entries[0].period.endDate
    );
    
    // Generate target periods
    const targetPeriods = this.generatePeriods(startDate, endDate, targetPeriod);
    
    // Create a map to aggregate entries into target periods
    const entriesByPeriod: CashflowMap = {};
    
    // Initialize result entries for each target period
    targetPeriods.forEach(period => {
      const periodKey = this.getPeriodKey(period);
      entriesByPeriod[periodKey] = {
        period,
        cashIn: 0,
        cashOut: 0,
        isProjected: true,
        cashInBreakdown: {},
        cashOutBreakdown: {}
      };
    });
    
    // Aggregate entries into target periods
    entries.forEach(entry => {
      // For each source entry, distribute its values to all overlapping target periods
      targetPeriods.forEach(targetPeriod => {
        if (this.periodsOverlap(entry.period, targetPeriod)) {
          const overlapRatio = this.calculatePeriodOverlapRatio(entry.period, targetPeriod);
          const periodKey = this.getPeriodKey(targetPeriod);
          
          // Add weighted values to the target period
          entriesByPeriod[periodKey].cashIn += entry.cashIn * overlapRatio;
          entriesByPeriod[periodKey].cashOut += entry.cashOut * overlapRatio;
          
          // Merge projected status (if any entry is actual data, target is actual)
          if (!entry.isProjected) {
            entriesByPeriod[periodKey].isProjected = false;
          }
          
          // Merge breakdowns if available
          if (entry.cashInBreakdown) {
            Object.entries(entry.cashInBreakdown).forEach(([key, value]) => {
              entriesByPeriod[periodKey].cashInBreakdown![key] = 
                (entriesByPeriod[periodKey].cashInBreakdown![key] || 0) + (value * overlapRatio);
            });
          }
          
          if (entry.cashOutBreakdown) {
            Object.entries(entry.cashOutBreakdown).forEach(([key, value]) => {
              entriesByPeriod[periodKey].cashOutBreakdown![key] = 
                (entriesByPeriod[periodKey].cashOutBreakdown![key] || 0) + (value * overlapRatio);
            });
          }
        }
      });
    });
    
    // Convert map back to array and sort chronologically
    const result = Object.values(entriesByPeriod).sort(
      (a, b) => a.period.startDate.getTime() - b.period.startDate.getTime()
    );
    
    // Calculate derived fields
    let cumCashFlow = 0;
    result.forEach(entry => {
      entry.cashFlow = entry.cashIn - entry.cashOut;
      cumCashFlow += entry.cashFlow;
      entry.cumCashFlow = cumCashFlow;
    });
    
    return result;
  }
  
  /**
   * Helper method to check if two periods overlap
   */
  private periodsOverlap(period1: DatePeriod, period2: DatePeriod): boolean {
    return (
      (period1.startDate <= period2.endDate) && 
      (period1.endDate >= period2.startDate)
    );
  }
  
  /**
   * Calculate the ratio of overlap between two periods
   * Used for weighted distribution of values
   */
  private calculatePeriodOverlapRatio(sourcePeriod: DatePeriod, targetPeriod: DatePeriod): number {
    // Find overlap start and end
    const overlapStart = new Date(
      Math.max(sourcePeriod.startDate.getTime(), targetPeriod.startDate.getTime())
    );
    
    const overlapEnd = new Date(
      Math.min(sourcePeriod.endDate.getTime(), targetPeriod.endDate.getTime())
    );
    
    // Calculate durations in milliseconds
    const sourceMs = sourcePeriod.endDate.getTime() - sourcePeriod.startDate.getTime();
    const overlapMs = overlapEnd.getTime() - overlapStart.getTime();
    
    // Avoid division by zero
    if (sourceMs <= 0) {
      return 0;
    }
    
    return overlapMs / sourceMs;
  }
  
  /**
   * Generate a unique key for a period to use in maps
   */
  private getPeriodKey(period: DatePeriod): string {
    switch (period.type) {
      case TimePeriod.DAILY:
        return this.formatDate(period.startDate);
      case TimePeriod.MONTHLY:
        return `${period.startDate.getFullYear()}-${period.startDate.getMonth() + 1}`;
      case TimePeriod.QUARTERLY:
        const quarter = Math.floor(period.startDate.getMonth() / 3) + 1;
        return `${period.startDate.getFullYear()}-Q${quarter}`;
      case TimePeriod.YEARLY:
        return period.startDate.getFullYear().toString();
      default:
        return `${period.startDate.getTime()}-${period.endDate.getTime()}`;
    }
  }
  
  /**
   * Format a date as YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }
  
  /**
   * Normalize a date to the start of its period
   */
  private normalizeDateToStartOfPeriod(date: Date, periodType: TimePeriod): Date {
    const result = new Date(date);
    
    switch (periodType) {
      case TimePeriod.YEARLY:
        result.setMonth(0, 1);
        result.setHours(0, 0, 0, 0);
        break;
      case TimePeriod.QUARTERLY:
        const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
        result.setMonth(quarterStartMonth, 1);
        result.setHours(0, 0, 0, 0);
        break;
      case TimePeriod.MONTHLY:
        result.setDate(1);
        result.setHours(0, 0, 0, 0);
        break;
      case TimePeriod.DAILY:
        result.setHours(0, 0, 0, 0);
        break;
    }
    
    return result;
  }
  
  /**
   * Normalize a date to the end of its period
   */
  private normalizeDateToEndOfPeriod(date: Date, periodType: TimePeriod): Date {
    const result = new Date(date);
    
    switch (periodType) {
      case TimePeriod.YEARLY:
        result.setMonth(11, 31);
        result.setHours(23, 59, 59, 999);
        break;
      case TimePeriod.QUARTERLY:
        const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
        result.setMonth(quarterStartMonth + 2, 0); // Last day of 3rd month
        result.setDate(new Date(date.getFullYear(), quarterStartMonth + 3, 0).getDate());
        result.setHours(23, 59, 59, 999);
        break;
      case TimePeriod.MONTHLY:
        result.setMonth(result.getMonth() + 1, 0); // Last day of month
        result.setHours(23, 59, 59, 999);
        break;
      case TimePeriod.DAILY:
        result.setHours(23, 59, 59, 999);
        break;
    }
    
    return result;
  }
  
  /**
   * Calculate the end date for a period starting at the specified date
   */
  private calculatePeriodEndDate(startDate: Date, periodType: TimePeriod): Date {
    return this.normalizeDateToEndOfPeriod(startDate, periodType);
  }
  
  /**
   * Add a number of periods to a date
   */
  private addToPeriod(date: Date, periodType: TimePeriod, amount: number): Date {
    const result = new Date(date);
    
    switch (periodType) {
      case TimePeriod.YEARLY:
        result.setFullYear(result.getFullYear() + amount);
        break;
      case TimePeriod.QUARTERLY:
        result.setMonth(result.getMonth() + (3 * amount));
        break;
      case TimePeriod.MONTHLY:
        result.setMonth(result.getMonth() + amount);
        break;
      case TimePeriod.DAILY:
        result.setDate(result.getDate() + amount);
        break;
    }
    
    return result;
  }
} 